"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ImageUpload } from "@/components/ImageUpload";
import { FileService } from "@/services/FileService";
import { FilePurpose } from "@/interfaces/files";
import UnifiedProductSelector from "@/components/UnifiedProductSelector";
import type { UnifiedProductItem } from "@/components/UnifiedProductSelector";
import { DateTimePicker } from "@/components/DatetimePickerComponent";
import CurrencyInput from "@/components/CurrencyInput";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
} from "@/components/ui/form";
import { OffersService } from "@/services/OffersService";
import { ProductsService } from "@/services/ProductsService";
import { numberToMoney } from "@/util/moneyConversion";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const schema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  description: z.string().optional(),
  products: z
    .array(
      z.object({
        product_id: z.string(),
        quantity: z.number().min(1, "Quantidade deve ser maior que 0"),
      })
    )
    .min(1, "Selecione ao menos um produto") as z.ZodType<UnifiedProductItem[]>,
  unitPrice: z.preprocess(
    (val) => Number(val),
    z.number().min(0, "Preço inválido")
  ),
  startsAt: z.date(),
  endsAt: z.date().optional().nullable(),
});

export type OfferFormValues = z.infer<typeof schema>;

export default function OfferForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [unitCost, setUnitCost] = useState(0);
  const [totalValue, setTotalValue] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [initialImage, setInitialImage] = useState<string | undefined>(
    undefined
  );
  const [existingFileId, setExistingFileId] = useState<string | undefined>();

  const form = useForm<OfferFormValues, undefined, OfferFormValues>({
    resolver: zodResolver(schema) as Resolver<OfferFormValues, undefined, OfferFormValues>,
    defaultValues: {
      name: "",
      description: "",
      products: [],
      unitPrice: 0,
      startsAt: new Date(),
      endsAt: undefined,
    },
  });

  const productsService = useMemo(() => new ProductsService(), []);
  const offersService = useMemo(() => new OffersService(), []);
  const filesService = useMemo(() => new FileService(), []);

  const fetchOffer = useCallback(async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }
    try {
      const fetched = await offersService.findById(id, {
        expand: ["file"],
      });
      console.log("Oferta carregada:", fetched);
      setIsEditing(true);

      // Carregar imagem existente se disponível
      if (
        fetched.file &&
        typeof fetched.file === "object" &&
        fetched.file.url
      ) {
        console.log("Imagem encontrada:", fetched.file.url);
        setInitialImage(fetched.file.url);
        setExistingFileId(fetched.file.id);
      } else if (typeof fetched.file === "string") {
        console.log("Imagem encontrada (string):", fetched.file);
        setInitialImage(fetched.file);
      } else {
        console.log("Nenhuma imagem encontrada na oferta");
      }

      // Garantir que temos o fileId se disponível
      if (fetched.fileId) {
        setExistingFileId(fetched.fileId);
      }

      form.reset({
        name: fetched.name,
        description: fetched.description,
        products: fetched.products.map((p) => ({
          product_id: p.productId,
          quantity: p.quantity || 1, // Use the actual quantity from the offer
        })),
        unitPrice: fetched.unitPrice || 0,
        startsAt: fetched.startsAt ? new Date(fetched.startsAt) : new Date(),
        endsAt: fetched.endsAt ? new Date(fetched.endsAt) : undefined,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, form, offersService]);

  useEffect(() => {
    fetchOffer();
  }, [fetchOffer]);

  const watchedProducts = form.watch("products");

  useEffect(() => {
    const calculateCost = async () => {
      if (!watchedProducts || watchedProducts.length === 0) {
        setUnitCost(0);
        setTotalValue(0);
        return;
      }

      try {
        let totalCost = 0;
        let totalValue = 0;
        for (const item of watchedProducts) {
          if (!item.product_id) continue;
          const product = await productsService.findById(item.product_id);
          totalCost += (product.unitCost || 0) * item.quantity;
          totalValue += (product.unitPrice || 0) * item.quantity;
        }
        setUnitCost(totalCost);
        setTotalValue(totalValue);
      } catch (error) {
        console.error("Erro ao calcular custo:", error);
      }
    };

    calculateCost();
  }, [watchedProducts, productsService]);

  const { handleSubmit, watch } = form;

  const onSubmit = async (data: OfferFormValues) => {
    if (data.unitPrice < unitCost) {
      form.setError("unitPrice", {
        message: "Preço da oferta não pode ser menor que o custo total",
      });
      return;
    }

    try {
      let fileId = existingFileId;

      // Upload da nova imagem se selecionada
      if (selectedFile) {
        console.log("Fazendo upload da imagem...");
        const uploadResponse = await filesService.upload({
          file: selectedFile,
          purpose: FilePurpose.OFFERS,
        });
        console.log("Upload realizado com sucesso:", uploadResponse);
        fileId = uploadResponse.id;
      }

      const payload = {
        name: data.name,
        description: data.description ?? "",
        products: data.products.map((p) => ({
          product_id: p.product_id!,
          quantity: p.quantity,
        })),
        unitPrice: data.unitPrice,
        startsAt: data.startsAt.toISOString(),
        endsAt: data.endsAt ? data.endsAt.toISOString() : undefined,
        fileId: fileId, // Adicionar o campo fileId
      };

      console.log("Payload da oferta:", payload);

      if (isEditing && id) {
        await offersService.update(id, payload);
      } else {
        await offersService.create(payload);
      }
      navigate("/ofertas");
    } catch (error) {
      console.error("Erro ao salvar oferta:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="max-w-4xl mx-auto">
          <Card className="overflow-hidden border-none shadow-md">
            <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
              <div className="flex items-center gap-3">
                <CardTitle className="text-xl font-semibold">
                  {isEditing ? "Editar Oferta" : "Nova Oferta"}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-6">
              {/* Nome da Oferta */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Nome da Oferta
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="h-12 text-base"
                        placeholder="Digite o nome da oferta"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Imagem da Oferta */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Imagem da Oferta</label>
                <div className="w-full max-w-sm">
                  <ImageUpload
                    initialImage={initialImage}
                    onImageChange={(file) => {
                      console.log("Imagem selecionada:", file);
                      setSelectedFile(file);
                    }}
                  />
                </div>
                {selectedFile && (
                  <p className="text-sm text-gray-600">
                    Arquivo selecionado: {selectedFile.name}
                  </p>
                )}
              </div>

              {/* Descrição com Rich Text Editor */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Descrição
                    </FormLabel>
                    <FormControl>
                      <div className="min-h-[120px] border rounded-lg">
                        <RichTextEditor
                          value={field.value || ""}
                          onChange={field.onChange}
                          placeholder="Descreva os detalhes da oferta..."
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Seleção de Produtos */}
              <FormField
                control={form.control}
                name="products"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Produtos da Oferta
                    </FormLabel>
                    <FormControl>
                      <UnifiedProductSelector
                        mode="offers"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Selecione os produtos que farão parte da oferta"
                        showBadges={false}
                        showFinancialInfo={true}
                        showMarginInfo={false}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Grid de Preços */}
              <div className="grid gap-6 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="unitPrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Preço da Oferta
                      </FormLabel>
                      <FormControl>
                        <CurrencyInput
                          {...field}
                          className="h-12 text-base"
                          placeholder="0,00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Valor Total dos Produtos
                  </label>
                  <Input
                    readOnly
                    value={numberToMoney(totalValue)}
                    className="h-12 text-base bg-blue-50 text-blue-700 border-blue-200"
                  />
                  <div className="text-xs text-gray-500">
                    Soma dos preços × quantidades
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Custo Total dos Produtos
                  </label>
                  <Input
                    readOnly
                    value={numberToMoney(unitCost)}
                    className="h-12 text-base bg-gray-50 text-gray-700"
                  />
                  <div className="text-xs text-gray-500">
                    Soma dos custos × quantidades
                  </div>
                </div>
              </div>

              {/* Análise de Desconto e Margem */}
              {totalValue > 0 && (
                <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">
                        Desconto da Oferta
                      </h4>
                      <div className="text-lg font-bold text-orange-600">
                        {numberToMoney(totalValue - watch("unitPrice"))}
                      </div>
                      <div className="text-xs text-gray-500">
                        {totalValue > 0
                          ? `${(
                              ((totalValue - watch("unitPrice")) / totalValue) *
                              100
                            ).toFixed(1)}% de desconto`
                          : "0% de desconto"}
                      </div>
                    </div>

                    <div className="text-center">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">
                        Margem de Lucro
                      </h4>
                      <div
                        className={`text-lg font-bold ${
                          watch("unitPrice") - unitCost >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {numberToMoney(watch("unitPrice") - unitCost)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {watch("unitPrice") > 0
                          ? `${(
                              ((watch("unitPrice") - unitCost) /
                                watch("unitPrice")) *
                              100
                            ).toFixed(1)}% margem`
                          : "0% margem"}
                      </div>
                    </div>

                    <div className="text-center">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">
                        Economia do Cliente
                      </h4>
                      <div className="text-lg font-bold text-blue-600">
                        {(
                          ((totalValue - watch("unitPrice")) / totalValue) *
                          100
                        ).toFixed(1)}
                        %
                      </div>
                      <div className="text-xs text-gray-500">
                        Cliente economiza vs. produtos individuais
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Período da Oferta */}
              <div className="grid gap-6 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="startsAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Data de Início
                      </FormLabel>
                      <FormControl>
                        <div className="w-full">
                          <DateTimePicker
                            selected={field.value}
                            onSelect={(date) => date && field.onChange(date)}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endsAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Data de Fim (Opcional)
                      </FormLabel>
                      <FormControl>
                        <div className="w-full">
                          <DateTimePicker
                            selected={field.value || undefined}
                            onSelect={field.onChange}
                          />
                        </div>
                      </FormControl>
                      <div className="text-xs text-gray-500 mt-1">
                        Deixe em branco se a oferta não tem data de expiração
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
            <CardFooter className="border-t p-4 sm:p-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/ofertas")}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                Salvar
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}






